import { useEffect, useRef } from 'react';
import { FormikErrors } from 'formik';

/**
 * Scrolls to the first field with a validation error after a failed submit attempt.
 * Uses Formik's submitCount to detect new submissions and isSubmitting to wait
 * until validation is complete.
 */
export function useScrollToError(
  errors: FormikErrors<any>,
  submitCount: number,
  isSubmitting: boolean,
) {
  const prevSubmitCount = useRef(submitCount);

  useEffect(() => {
    // Only act when a new submit attempt has completed (submitCount incremented and not currently submitting)
    if (submitCount === prevSubmitCount.current) return;
    prevSubmitCount.current = submitCount;

    // If submitting, validation passed — no need to scroll
    if (isSubmitting) return;

    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    // Find the first field element with an error
    for (const key of errorKeys) {
      const el =
        document.getElementById(key) ||
        document.querySelector(`[name="${key}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (el instanceof HTMLElement) {
          el.focus({ preventScroll: true });
        }
        return;
      }
    }
  }, [submitCount, isSubmitting, errors]);
}
